import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { OfferBaseData, OfferBaseDataPatch } from "../types/base-data";

/**
 * A save refetches the entry either way: on error to restore the server
 * state over the optimistic value, on success to pick up what the server
 * derived from it. See the create-auto-saving-input convention.
 */
const invalidatesOfferBaseData = (
  _result: unknown,
  _error: unknown,
  request: { id: UUID },
) =>
  [
    { type: "OmniOfferBaseData" as const, id: request.id },
    { type: "OmniOfferBaseData" as const, id: "LIST" },
  ] as const;

export const omniOfferBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["OmniOfferBaseData", "DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      omniOfferBaseDataCreate: build.mutation<OfferBaseData, CreateRequest>({
        query: (request) => ({
          url: "/omni/offer/base-data",
          method: "POST",
          body: request,
        }),
        invalidatesTags: [{ type: "OmniOfferBaseData", id: "LIST" }],
      }),
      omniOfferBaseDataShow: build.query<OfferBaseData, ShowRequest>({
        query: (request) => ({
          url: "/omni/offer/base-data",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("OmniOfferBaseData"),
      }),
      omniOfferBaseDataList: build.query<Lst<OfferBaseData>, ListRequest>({
        query: (request) => ({
          url: "/omni/offer/base-data/list",
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) =>
          providesList(result?.items, "OmniOfferBaseData"),
      }),
      omniOfferBaseDataPatch: build.mutation<OfferBaseData, PatchRequest>({
        query: (request) => ({
          url: "/omni/offer/base-data",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesOfferBaseData,
      }),
      omniOfferBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/omni/offer/base-data",
          method: "DELETE",
          body: request,
        }),
        // The backend refuses the delete while the instance is a
        // default, so the defaults are refetched either way.
        invalidatesTags: [
          { type: "OmniOfferBaseData", id: "LIST" },
          "DefaultBaseData",
        ],
      }),
    }),
  });

export const {
  useOmniOfferBaseDataCreateMutation,
  useOmniOfferBaseDataShowQuery,
  useOmniOfferBaseDataListQuery,
  useOmniOfferBaseDataPatchMutation,
  useOmniOfferBaseDataDeleteMutation,
} = omniOfferBaseDataApi;

export interface CreateRequest {
  id: UUID;
  companyId: UUID;
  serviceKind: string;
  label: string;
}

export interface ShowRequest {
  id: UUID;
}

export interface ListRequest {
  /** Required by the backend: active, with at least one company. */
  company: Filter<UUID>;
  serviceKind: Filter<string>;
  label?: Filter<string>;
  orderBy?: OrdrBy<"id" | "label" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
}

export type PatchRequest = OfferBaseDataPatch & { id: UUID };

export interface DeleteRequest {
  id: UUID;
}

export const updateOmniOfferBaseDataCache = updateCache(
  omniOfferBaseDataApi,
  "omniOfferBaseDataShow",
);
